import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import Creatable from 'react-select/creatable';
import {
  IInputProps,
  ISelectInputProps,
  ISelectOption,
} from 'types/components/inputs/input.interface';
import InputWrapper from '../wrapper/InputWrapper';
import './SelectInput.scss';

const useFixReactSelectViewport = (isMenuOpen: boolean) => {
  useEffect(() => {
    if (!isMenuOpen) return;
    if (!window.visualViewport) return;

    let frame = 0;

    const updateMenuPosition = () => {
      cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        const control = document.querySelector(
          '.react-select__control--menu-is-open',
        ) as HTMLElement | null;

        const menu = document.querySelector('.react-select__menu') as HTMLElement | null;

        if (!control || !menu) return;

        const rect = control.getBoundingClientRect();
        const viewport = window.visualViewport;

        if (!viewport) return;

        const spaceBelow = viewport.height - rect.bottom;
        const spaceAbove = rect.top;
        const shouldOpenUp = spaceBelow < 220 && spaceAbove > spaceBelow;

        menu.style.position = 'fixed';
        menu.style.left = `${rect.left}px`;
        menu.style.width = `${rect.width}px`;
        menu.style.maxWidth = `${rect.width}px`;
        menu.style.zIndex = '9999';

        if (shouldOpenUp) {
          menu.style.top = 'auto';
          menu.style.bottom = `${viewport.height - rect.top}px`;
        } else {
          menu.style.bottom = 'auto';
          menu.style.top = `${rect.bottom}px`;
        }
      });
    };

    updateMenuPosition();

    window.visualViewport.addEventListener('resize', updateMenuPosition);
    window.visualViewport.addEventListener('scroll', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);

    return () => {
      cancelAnimationFrame(frame);
      window.visualViewport?.removeEventListener('resize', updateMenuPosition);
      window.visualViewport?.removeEventListener('scroll', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isMenuOpen]);
};

const SelectInput = <T,>({
  isCreatable = false,
  isAsync = false,
  isClearable = true,
  onChange,
  value,
  ...props
}: ISelectInputProps<T> & { isClearable?: boolean }) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const CustomSelect = isCreatable ? (isAsync ? AsyncCreatableSelect : Creatable) : Select;

  useFixReactSelectViewport(menuIsOpen);

  const handleChange = (newValue: unknown, actionMeta: unknown) => {
    if (newValue === null || newValue === undefined) {
      onChange?.({ label: '', value: '' } as ISelectOption, actionMeta);
    } else {
      onChange?.(newValue as ISelectOption, actionMeta);
    }
  };

  const customStyles = useMemo(
    () => ({
      menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
      }),
      menu: (base: any) => ({
        ...base,
        zIndex: 9999,
        backgroundColor: 'var(--background)',
        marginTop: 4,
        borderRadius: 8,
        overflow: 'hidden',
      }),
      menuList: (base: any) => ({
        ...base,
        maxHeight: 240,
        WebkitOverflowScrolling: 'touch',
      }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected
          ? 'var(--primary)'
          : state.isFocused
            ? 'color-mix(in srgb, var(--primary) 30%, transparent)'
            : 'var(--background)',
        color: state.isSelected ? 'var(--app-background)' : 'var(--text)',
      }),
    }),
    [],
  );
  return (
    <InputWrapper
      disabled={!!props.displayMode ? false : !!props.isDisabled}
      {...(props as IInputProps)}
      value={value ? `${value.value}` : undefined}
      render={(inputProps) => (
        <CustomSelect
          {...props}
          {...inputProps}
          value={value}
          placeholder={props.placeholder ?? i18next.t('input.placeholder.select-default')}
          isDisabled={!!props.displayMode ? true : !!props.isDisabled}
          isClearable={value?.value !== undefined ? isClearable : false}
          onChange={handleChange}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => setMenuIsOpen(false)}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          menuPlacement="auto"
          styles={customStyles}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      )}
    />
  );
};

export default SelectInput;
